import { useShortRoundInfo } from './archive';
import type { Round } from './model';
import { sublineModel } from './subline-model';
import { Fragment, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

const BASE_CLASS = 'text-xs text-muted-foreground tabular-nums';
const RESULT_CLASS = { up: 'text-beneficial-foreground', down: 'text-destructive' };
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** One muted line for a known round: window, baseline, final price, result (see sublineModel). */
export const RoundSubline = ({ round, timezone, className }: { round: Round; timezone: string; className?: string }) => {
  // No Suspense: this is the first btc5m consumer outside the 5-minute route (home cards, wallet modal), and
  // suspending here would flash the nearest boundary (the home route, or the whole app for the wallet modal).
  const { t, ready } = useTranslation('btc5m', { useSuspense: false });
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const now = Math.floor(Date.now() / 1000);
  const model = sublineModel(round, timezone, now);
  // An open round turns pending at endAt even when nothing refetches the feed (a wallet row has no poll).
  const openUntil = model.kind === 'live' && model.phase === 'open' ? round.endAt : undefined;
  useEffect(() => {
    if (openUntil === undefined) return;
    const id = setTimeout(tick, Math.min(Math.max(0, openUntil * 1000 - Date.now()) + 50, MAX_TIMEOUT_MS));
    return () => clearTimeout(id);
  }, [openUntil]);
  if (!ready) return null;
  const parts =
    model.kind === 'live'
      ? [t('subline.baseline', { price: model.baseline }), t(`phase.${model.phase}`)]
      : model.kind === 'resolved'
        ? [
            `${t('subline.baseline', { price: model.baseline })} → ${t('subline.final', { price: model.final })}`,
            model.result && <span className={RESULT_CLASS[model.result]}>{t(`result.${model.result}`)}</span>,
          ].filter(Boolean)
        : [];
  return (
    <p data-testid="short-round-subline" className={className ? `${BASE_CLASS} ${className}` : BASE_CLASS}>
      {model.window}
      {parts.map((part, i) => (
        <Fragment key={i}>
          {' · '}
          {part}
        </Fragment>
      ))}
    </p>
  );
};

/**
 * The summary line under a 5-minute round on a list card or a wallet row. `title`: the row's event title and/or
 * market name. Draws nothing for a market that is not a round, a round without a file, or while it loads.
 */
export const ShortRoundSubline = ({
  eventId,
  title,
  className,
}: {
  eventId?: string;
  title?: string | readonly (string | undefined)[];
  className?: string;
}) => {
  const info = useShortRoundInfo(eventId, title);
  return info ? <RoundSubline round={info.round} timezone={info.timezone} className={className} /> : null;
};
